import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { RadioGroup, Radio } from '../../../components/ui/radio';
import ReactApexChart from 'react-apexcharts';
import { PIPEFITTER_CHART_COLORS } from '../constants/uiConstants';

interface PipeLinearGraphProps {
  pipeDataGraph: any[];
  projectNumber?: string;
  isLoading?: boolean;
}

export const PipeLinearGraph: React.FC<PipeLinearGraphProps> = ({
  pipeDataGraph,
  isLoading,
}) => {
  const [showIDData, setShowIDData] = useState(true);

  const chartData = useMemo(() => {
    if (!pipeDataGraph || pipeDataGraph.length === 0) {
      return { series: [], categories: [] };
    }
    const categories = pipeDataGraph.map((d: any) => d.pipeEndID ?? '');
    const avgData = pipeDataGraph.map((d: any) => {
      const val = showIDData ? d.avgID : d.avgOD;
      return val != null ? parseFloat(String(val)) : null;
    });
    const minData = pipeDataGraph.map((d: any) => {
      const val = showIDData ? d.minID : d.minOD;
      return val != null ? parseFloat(String(val)) : null;
    });
    const maxData = pipeDataGraph.map((d: any) => {
      const val = showIDData ? d.maxID : d.maxOD;
      return val != null ? parseFloat(String(val)) : null;
    });
    return {
      series: [
        { name: 'Avg', data: avgData },
        { name: 'Min', data: minData },
        { name: 'Max', data: maxData },
      ],
      categories,
    };
  }, [pipeDataGraph, showIDData]);

  const options = useMemo(
    () => ({
      grid: {
        borderColor: PIPEFITTER_CHART_COLORS.border,
      },
      chart: {
        height: 350,
        type: 'line' as const,
        foreColor: PIPEFITTER_CHART_COLORS.foreground,
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      stroke: {
        width: 2,
        curve: 'smooth' as const,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'category' as const,
        categories: chartData.categories,
        title: {
          text: 'Pipe position',
        },
        labels: {
          rotate: -45,
          maxWidth: 80,
        },
      },
      yaxis: {
        title: {
          text: showIDData ? 'ID (mm)' : 'OD (mm)',
        },
        tickAmount: 6,
      },
      tooltip: {
        theme: 'dark' as const,
      },
      colors: [PIPEFITTER_CHART_COLORS.primary, PIPEFITTER_CHART_COLORS.gray, PIPEFITTER_CHART_COLORS.secondaryGreen],
      legend: {
        show: true,
        position: 'top' as const,
      },
    }),
    [chartData.categories, showIDData]
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500">Loading pipe linear graph...</p>
        </CardContent>
      </Card>
    );
  }

  if (!pipeDataGraph || pipeDataGraph.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8 text-gray-500">No data available</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Label>Measure</Label>
            <RadioGroup
              value={showIDData ? 'id' : 'od'}
              onChange={(value) => setShowIDData(value === 'id')}
            >
              <Radio value="id">ID</Radio>
              <Radio value="od">OD</Radio>
            </RadioGroup>
          </div>
          <ReactApexChart
            options={options}
            series={chartData.series}
            type="line"
            height={350}
          />
        </div>
      </CardContent>
    </Card>
  );
};
